import type { ActivityLog, AppSettings, DailyStat, DomainItem, DomainStatus, RedirectType, TrendPoint } from "@/types";

const SOURCE_LABELS = ["直接访问", "搜索引擎", "社交平台", "外部链接"] as const;

export const DEFAULT_SETTINGS: AppSettings = {
  defaultRedirectType: "302",
  fallbackUrl: "https://yourbrand.com/404",
  autoRedirect: true,
  trackClicks: true,
};

export type ActionResult = { ok: true; message?: string } | { ok: false; message: string };

export interface ImportRow {
  domain: string;
  targetUrl: string;
  redirectType: RedirectType;
  remark: string;
}

export interface RelaySnapshot {
  domains: DomainItem[];
  settings: AppSettings;
  stats: DailyStat[];
  activities: ActivityLog[];
  ready: boolean;
  loadError: string;
}

const listeners = new Set<() => void>();

function emptySnapshot(): RelaySnapshot {
  return {
    domains: [],
    settings: { ...DEFAULT_SETTINGS },
    stats: [],
    activities: [],
    ready: false,
    loadError: "",
  };
}

let snapshot: RelaySnapshot = emptySnapshot();
let loading: Promise<void> | null = null;

function emit() {
  listeners.forEach((listener) => listener());
}

function applyState(state: Pick<RelaySnapshot, "domains" | "settings" | "stats" | "activities">) {
  snapshot = {
    domains: state.domains,
    settings: state.settings,
    stats: state.stats,
    activities: state.activities,
    ready: true,
    loadError: "",
  };
  emit();
}

async function request(path: string, body?: unknown): Promise<Record<string, unknown>> {
  const response = await fetch(path, {
    method: body === undefined ? "GET" : "POST",
    credentials: "same-origin",
    headers: body === undefined ? undefined : { "Content-Type": "application/json" },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  const data = (await response.json()) as Record<string, unknown>;
  if (data.state && typeof data.state === "object") {
    applyState(data.state as RelaySnapshot);
  }
  if (!response.ok && data.ok !== false) {
    return { ok: false, message: "请求没有完成" };
  }
  return data;
}

export function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function getSnapshot() {
  return snapshot;
}

export function resetRelay() {
  snapshot = emptySnapshot();
  loading = null;
  emit();
}

export function ensureLoaded() {
  if (snapshot.ready) return Promise.resolve();
  if (!loading) {
    loading = request("/api/state")
      .then((data) => {
        if (!data.state) {
          snapshot = { ...snapshot, ready: true, loadError: String(data.message || "暂时读不到跳转规则") };
          emit();
        }
      })
      .catch(() => {
        snapshot = { ...snapshot, ready: true, loadError: "跳转服务没有连上" };
        emit();
      })
      .finally(() => {
        loading = null;
      });
  }
  return loading;
}

function fail(data: Record<string, unknown>, fallback: string): ActionResult {
  return { ok: false, message: String(data.message || fallback) };
}

export async function addDomain(payload: Omit<DomainItem, "id" | "clicks" | "createdAt">): Promise<ActionResult> {
  try {
    const data = await request("/api/domains", payload);
    return data.ok ? { ok: true } : fail(data, "没有保存成功");
  } catch {
    return { ok: false, message: "跳转服务没有连上" };
  }
}

export async function updateDomain(id: string, payload: Partial<DomainItem>): Promise<ActionResult> {
  try {
    const data = await request("/api/domains/update", { id, payload });
    return data.ok ? { ok: true } : fail(data, "没有保存成功");
  } catch {
    return { ok: false, message: "跳转服务没有连上" };
  }
}

export async function toggleStatus(id: string) {
  const current = snapshot.domains.find((item) => item.id === id);
  if (!current) return;
  const status: DomainStatus = current.status === "active" ? "paused" : "active";
  await updateDomain(id, { status });
}

export async function removeDomains(ids: string[]) {
  try {
    await request("/api/domains/delete", { ids });
  } catch {
    // the page can retry
  }
}

export async function batchSetStatus(ids: string[], status: DomainStatus) {
  try {
    const data = await request("/api/domains/status", { ids, status });
    return Number(data.count) || 0;
  } catch {
    return 0;
  }
}

export async function batchRetarget(ids: string[], targetUrl: string): Promise<ActionResult> {
  try {
    const data = await request("/api/domains/retarget", { ids, targetUrl });
    return data.ok ? { ok: true } : fail(data, "没有修改成功");
  } catch {
    return { ok: false, message: "跳转服务没有连上" };
  }
}

export async function importDomains(rows: ImportRow[]) {
  try {
    const data = await request("/api/domains/import", { rows });
    return {
      added: Number(data.added) || 0,
      skipped: Array.isArray(data.skipped) ? (data.skipped as string[]) : [],
      message: String(data.message || ""),
    };
  } catch {
    return { added: 0, skipped: [], message: "跳转服务没有连上" };
  }
}

export async function saveSettings(next: AppSettings): Promise<ActionResult> {
  try {
    const data = await request("/api/settings", next);
    return data.ok ? { ok: true } : fail(data, "没有保存成功");
  } catch {
    return { ok: false, message: "跳转服务没有连上" };
  }
}

export async function changePassword(current: string, next: string): Promise<ActionResult> {
  try {
    const data = await request("/api/password", { current, next });
    return data.ok ? { ok: true, message: String(data.message || "密码已更新") } : fail(data, "没有修改成功");
  } catch {
    return { ok: false, message: "跳转服务没有连上" };
  }
}

export function isoDate(date: Date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function addDays(date: Date, days: number) {
  const next = new Date(date);
  next.setDate(next.getDate() + days);
  return next;
}

function emptyDay(date: string): DailyStat {
  return { date, clicks: 0, unique: 0, byDomain: {}, sources: {} };
}

export function normalizeDomain(raw: string) {
  return raw
    .trim()
    .toLowerCase()
    .replace(/^https?:\/\//, "")
    .split("/")[0]
    .replace(/:\d+$/, "")
    .replace(/\.$/, "");
}

export function isValidDomain(raw: string) {
  const domain = normalizeDomain(raw);
  return /^(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,}$/.test(domain);
}

export function isHttpUrl(raw: string) {
  try {
    const url = new URL(raw.trim());
    if (url.protocol !== "http:" && url.protocol !== "https:") return false;
    return url.hostname === "localhost" || url.hostname.includes(".");
  } catch {
    return false;
  }
}

export function isRedirectType(value: string): value is RedirectType {
  return value === "301" || value === "302" || value === "mask";
}

export function windowStats(stats: DailyStat[], days: number) {
  const map = new Map(stats.map((day) => [day.date, day]));
  const result: DailyStat[] = [];
  for (let offset = days - 1; offset >= 0; offset -= 1) {
    const date = isoDate(addDays(new Date(), -offset));
    result.push(map.get(date) ?? emptyDay(date));
  }
  return result;
}

export function toTrend(days: DailyStat[]): TrendPoint[] {
  return days.map((day) => ({
    date: `${day.date.slice(5, 7)}/${day.date.slice(8, 10)}`,
    clicks: day.clicks,
    unique: day.unique,
  }));
}

export function sumClicks(days: DailyStat[]) {
  return days.reduce((sum, day) => sum + day.clicks, 0);
}

export function percentDelta(current: number, previous: number) {
  if (previous <= 0) {
    return current > 0 ? { text: "新增", trend: "up" as const } : { text: "0%", trend: "up" as const };
  }
  const pct = ((current - previous) / previous) * 100;
  const trend = pct >= 0 ? ("up" as const) : ("down" as const);
  const sign = pct > 0 ? "+" : "";
  return { text: `${sign}${pct.toFixed(1)}%`, trend };
}

export function topInRange(domains: DomainItem[], stats: DailyStat[], days: number, limit = 5) {
  const windowed = windowStats(stats, days);
  return domains
    .map((domain) => ({
      domain: domain.domain,
      clicks: windowed.reduce((sum, day) => sum + (day.byDomain[domain.id]?.clicks ?? 0), 0),
    }))
    .sort((a, b) => b.clicks - a.clicks)
    .slice(0, limit);
}

export function sourceTotals(stats: DailyStat[], days: number) {
  const windowed = windowStats(stats, days);
  return SOURCE_LABELS.map((label) => ({
    label,
    count: windowed.reduce((sum, day) => sum + (day.sources?.[label] ?? 0), 0),
  }));
}

export function jumpUrl(id: string) {
  const base = (typeof __BASE_PATH__ === "string" ? __BASE_PATH__ : "/").replace(/\/$/, "");
  return `${window.location.origin}${base}/go/${id}`;
}
